/**
 * Phase 2 앱 뼈대용 더미 카탈로그/시세/보유 데이터.
 *
 * 구조는 서버 스키마(db/peeka/sql/06_DDL_CARD_DOMAIN.sql)와 1:1 로 맞춘다 —
 * Phase 4 카드 조회 API 가 생기면 이 모듈만 src/api/cards.ts 호출로 교체한다.
 * 이미지 URL 은 아직 CDN 규약 미확정이라 색상 플레이스홀더(gradient key)로 대체.
 */
import { currentLanguage } from "../i18n";

export interface DummyCardSet {
  cardsetUid: string;
  cardsetCd: string;
  cardsetNm: string;
  srisNm: string;
  langCd: "ko" | "en" | "ja";
  totalCardCnt: number;
  relsDt: string;
}

export interface DummyVariantPrice {
  graderCd: "RAW" | "PSA" | "BGS" | "CGC";
  grdCd: string; // RAW 는 "-"
  prcAmt: number;
  currcyCd: "KRW";
  trendRt: number; // % (+상승 / -하락)
}

/**
 * 세부 버전 (기획확정안 §3.3).
 * ⚠ 전체 버전 목록을 공통으로 보여주지 않는다 — 식별된 언어판·세트·카드번호에
 * **실제 발행된 버전만** 제시한다. 버전이 하나뿐이면 선택 단계를 생략한다.
 */
export interface DummyVariant {
  /**
   * 서버 스키마의 (FINSH_CD, PATRN_CD) 조합에 대응한다.
   * 배리언트는 카드 행을 나누지 않는다 — 카드 1행 = 인쇄판(세트+번호+언어)이고,
   * 선택된 버전은 보유(cl_card_posesn)·가격(pr_card_varnt_prc)의 피니시/패턴 축이 된다.
   * ⚠ Phase 4 API 연동 시 이 필드는 finshCd/patrnCd 두 개로 분리된다.
   */
  varntCd: string;
  label: string;
  /** 구분 힌트 — "잘 모르겠어요" 분기에서 안내로 노출 */
  hint?: string;
}

/** SC-006b 유형 필터 축 — 포켓몬 카드의 3분류. */
export type DummyCardType = "POKEMON" | "TRAINER" | "ENERGY";

/** SC-006c Finish 필터 축 — 카드 표면. 시세가 갈리는 축이기도 하다(PRD §5.4 카드 변형). */
export type DummyFinish = "NORMAL" | "REVERSE_HOLO" | "HOLOFOIL";

/** SC-006e 에너지 필터 축. 트레이너·에너지 카드에는 없다. */
export type DummyEnergy =
  | "COLORLESS" | "DARKNESS" | "FIGHTING" | "FIRE" | "GRASS"
  | "LIGHTNING" | "METAL" | "PSYCHIC" | "WATER";

export interface DummyCard {
  cardUid: string;
  cardsetUid: string;
  cardNm: string;
  cardNo: string;
  langCd: "ko" | "en" | "ja";
  rarityCd: string;
  artistNm?: string;
  /** SC-006b 유형 */
  cardTypeCd: DummyCardType;
  /** SC-006c 이 카드가 실제로 발행된 표면. 여러 개면 그만큼 시세도 갈린다 */
  finishCds: DummyFinish[];
  /** SC-006e 에너지 타입 — POKEMON 만 보유 */
  energyCd?: DummyEnergy;
  /** SC-006f 정렬축(HP) — POKEMON 만 보유 */
  hp?: number;
  /** 플레이스홀더 그라디언트 키 (이미지 CDN 확정 전) */
  colorKey: string;
  listPrcAmt: number;
  variantPrices: DummyVariantPrice[];
  /** §3.3 이 카드에 실제 발행된 세부 버전. 1개면 선택 단계 생략 */
  variants: DummyVariant[];
}

export interface DummyHolding {
  holdingId: number;
  cardUid: string;
  qty: number;
  /** 구매가 — **더 이상 쓰지 않는다** (2026-08-25 FN-022 폐기). 화면·계산 어디에서도
   *  읽지 않고 항상 null 이 들어간다. 서버가 붙을 때 이 필드와 purchDt 를 함께 지운다. */
  purchPrcAmt: number | null;
  /**
   * **스캔한 시점의 대표 시세** (1장 기준). 구매가를 안 받는 대신 이 값을 원가처럼 쓴다 —
   * '수익률 순' 정렬이 성립하는 유일한 근거다 (IA rev28 · 2026-08-25).
   * ⚠ 스캔이 곧 구매는 아니다. 그래서 화면에서 이 기준을 '스캔한 시점의 시세 대비'
   *   라고 밝힌다 (BRAND §3.7 은 수치가 아니라 약속·수식어를 금지한다).
   * ⚠ 예전에 담긴 보유분에는 이 값이 없을 수 있다(undefined) — 정렬에서 뒤로 밀린다.
   */
  scanPrcAmt?: number;
  purchDt: string;
  graderCd: string;
  grdCd: string;
}

export interface DummyCollection {
  coletId: number;
  coletNm: string;
  cardUids: string[];
}

export const DUMMY_SETS: DummyCardSet[] = [
  { cardsetUid: "set-sv4a-ko", cardsetCd: "sv4a", cardsetNm: "샤이니 트레저 ex", srisNm: "스칼렛&바이올렛", langCd: "ko", totalCardCnt: 190, relsDt: "2024-01-26" },
  { cardsetUid: "set-sv8a-ko", cardsetCd: "sv8a", cardsetNm: "테라스탈 페스타 ex", srisNm: "스칼렛&바이올렛", langCd: "ko", totalCardCnt: 187, relsDt: "2025-01-10" },
  { cardsetUid: "set-s12a-ko", cardsetCd: "s12a", cardsetNm: "VSTAR 유니버스", srisNm: "소드&실드", langCd: "ko", totalCardCnt: 172, relsDt: "2023-01-13" },
];

export const DUMMY_CARDS: DummyCard[] = [
  {
    cardUid: "card-sv4a-205-ko", cardsetUid: "set-sv4a-ko",
    cardNm: "리자몽 ex", cardNo: "205/190", langCd: "ko", rarityCd: "SSR",
    artistNm: "PLANETA Igarashi", colorKey: "fire", listPrcAmt: 68000,
    cardTypeCd: "POKEMON", finishCds: ["HOLOFOIL"], energyCd: "FIRE", hp: 330,
    variantPrices: [
      { graderCd: "RAW", grdCd: "-", prcAmt: 68000, currcyCd: "KRW", trendRt: 4.2 },
      { graderCd: "PSA", grdCd: "10", prcAmt: 210000, currcyCd: "KRW", trendRt: 8.1 },
      { graderCd: "PSA", grdCd: "9", prcAmt: 112000, currcyCd: "KRW", trendRt: 1.5 },
    ],
    variants: [
      { varntCd: "DEFAULT", label: "기본판", hint: "표면에 특수 무늬가 없습니다" },
      { varntCd: "MIRROR_ENERGY", label: "에너지 마크 미러", hint: "카드 전면에 작은 에너지 마크가 반복됩니다" },
      { varntCd: "MIRROR_MASTERBALL", label: "마스터볼 미러", hint: "보라색 마스터볼 무늬가 전면에 반복됩니다" },
    ],
  },
  {
    cardUid: "card-sv4a-350-ko", cardsetUid: "set-sv4a-ko",
    cardNm: "피카츄", cardNo: "350/190", langCd: "ko", rarityCd: "SAR",
    artistNm: "kirisAki", colorKey: "thunder", listPrcAmt: 145000,
    cardTypeCd: "POKEMON", finishCds: ["HOLOFOIL"], energyCd: "LIGHTNING", hp: 70,
    variantPrices: [
      { graderCd: "RAW", grdCd: "-", prcAmt: 145000, currcyCd: "KRW", trendRt: -2.3 },
      { graderCd: "PSA", grdCd: "10", prcAmt: 520000, currcyCd: "KRW", trendRt: 12.7 },
    ],
    variants: [
      { varntCd: "DEFAULT", label: "기본판" },
    ],
  },
  {
    cardUid: "card-s12a-223-ko", cardsetUid: "set-s12a-ko",
    cardNm: "뮤 VMAX", cardNo: "223/172", langCd: "ko", rarityCd: "SAR",
    colorKey: "psychic", listPrcAmt: 89000,
    cardTypeCd: "POKEMON", finishCds: ["HOLOFOIL"], energyCd: "PSYCHIC", hp: 310,
    variantPrices: [
      { graderCd: "RAW", grdCd: "-", prcAmt: 89000, currcyCd: "KRW", trendRt: 0.8 },
      { graderCd: "PSA", grdCd: "10", prcAmt: 265000, currcyCd: "KRW", trendRt: 5.4 },
      { graderCd: "BGS", grdCd: "9.5", prcAmt: 198000, currcyCd: "KRW", trendRt: 3.1 },
    ],
    variants: [
      { varntCd: "DEFAULT", label: "기본판" },
      { varntCd: "MIRROR_MONSTERBALL", label: "몬스터볼 미러", hint: "빨강·흰색 몬스터볼 무늬가 반복됩니다" },
    ],
  },
  {
    cardUid: "card-s12a-172-ko", cardsetUid: "set-s12a-ko",
    cardNm: "아르세우스 VSTAR", cardNo: "172/172", langCd: "ko", rarityCd: "UR",
    colorKey: "steel", listPrcAmt: 36000,
    cardTypeCd: "POKEMON", finishCds: ["HOLOFOIL"], energyCd: "COLORLESS", hp: 280,
    variantPrices: [
      { graderCd: "RAW", grdCd: "-", prcAmt: 36000, currcyCd: "KRW", trendRt: -1.1 },
      { graderCd: "PSA", grdCd: "10", prcAmt: 98000, currcyCd: "KRW", trendRt: 2.0 },
    ],
    variants: [
      { varntCd: "DEFAULT", label: "기본판" },
    ],
  },
  {
    cardUid: "card-sv8a-091-ko", cardsetUid: "set-sv8a-ko",
    cardNm: "이브이 ex", cardNo: "091/187", langCd: "ko", rarityCd: "RR",
    colorKey: "normal", listPrcAmt: 4500,
    cardTypeCd: "POKEMON", finishCds: ["NORMAL", "REVERSE_HOLO"], energyCd: "COLORLESS", hp: 200,
    variantPrices: [
      { graderCd: "RAW", grdCd: "-", prcAmt: 4500, currcyCd: "KRW", trendRt: 0.2 },
    ],
    variants: [
      { varntCd: "DEFAULT", label: "논홀로", hint: "표면이 무광이고 반사가 없습니다" },
      { varntCd: "REVERSE_HOLO", label: "리버스홀로", hint: "일러스트를 제외한 배경이 반짝입니다" },
    ],
  },
  {
    // 트레이너 카드 — SC-006b 유형 필터가 실제로 걸리도록 카탈로그에 한 장 둔다.
    // 화면정의서 관측: 트레이너는 Finish 가 Normal 이고 에너지·HP 가 없다.
    cardUid: "card-sv8a-142-ko", cardsetUid: "set-sv8a-ko",
    cardNm: "박사의 연구", cardNo: "142/187", langCd: "ko", rarityCd: "RR",
    colorKey: "normal", listPrcAmt: 1800,
    cardTypeCd: "TRAINER", finishCds: ["NORMAL", "REVERSE_HOLO"],
    variantPrices: [
      { graderCd: "RAW", grdCd: "-", prcAmt: 1800, currcyCd: "KRW", trendRt: -0.4 },
    ],
    variants: [
      { varntCd: "DEFAULT", label: "논홀로" },
      { varntCd: "REVERSE_HOLO", label: "리버스홀로", hint: "일러스트를 제외한 배경이 반짝입니다" },
    ],
  },
  {
    cardUid: "card-sv8a-215-ko", cardsetUid: "set-sv8a-ko",
    cardNm: "테라파고스 ex", cardNo: "215/187", langCd: "ko", rarityCd: "SAR",
    colorKey: "grass", listPrcAmt: 172000,
    cardTypeCd: "POKEMON", finishCds: ["HOLOFOIL"], energyCd: "GRASS", hp: 230,
    variantPrices: [
      { graderCd: "RAW", grdCd: "-", prcAmt: 172000, currcyCd: "KRW", trendRt: 6.9 },
      { graderCd: "PSA", grdCd: "10", prcAmt: 610000, currcyCd: "KRW", trendRt: 15.2 },
    ],
    variants: [
      { varntCd: "DEFAULT", label: "기본판" },
    ],
  },
];

/* ============================================================================
 * 표시 언어별 이름 (2026-08-05)
 *
 * 포켓몬 카드는 언어판마다 **이름 자체가 다르다** (리자몽 / Charizard / リザードン).
 * 세트·시리즈명도 마찬가지다. 표시 언어를 일본어로 바꿔놓고 카드명만 한국어로
 * 남으면 화면이 반쪽만 번역된 것처럼 읽힌다.
 *
 * ⚠ 이건 **표시 언어**(설정) 축이지 **카드 에디션**(어느 시장 시세를 볼지) 축이
 *   아니다. 둘은 따로 움직인다 — 일본판 시세를 한국어 UI 로 볼 수 있다.
 *
 * 서버가 붙으면 이 표는 사라진다. 실제 API 는 요청 언어에 맞는 이름을 그대로
 * 내려주므로, 아래 게터는 그 동작을 미리 흉내 내는 자리다.
 * ========================================================================== */

type NameSet = { ko: string; en: string; ja: string };

const CARD_NAMES: Record<string, NameSet> = {
  "card-sv4a-205-ko": { ko: "리자몽 ex", en: "Charizard ex", ja: "リザードン ex" },
  "card-sv4a-350-ko": { ko: "피카츄", en: "Pikachu", ja: "ピカチュウ" },
  "card-s12a-223-ko": { ko: "뮤 VMAX", en: "Mew VMAX", ja: "ミュウ VMAX" },
  "card-s12a-172-ko": { ko: "아르세우스 VSTAR", en: "Arceus VSTAR", ja: "アルセウス VSTAR" },
  "card-sv8a-091-ko": { ko: "이브이 ex", en: "Eevee ex", ja: "イーブイ ex" },
  "card-sv8a-142-ko": { ko: "박사의 연구", en: "Professor's Research", ja: "博士の研究" },
  "card-sv8a-215-ko": { ko: "테라파고스 ex", en: "Terapagos ex", ja: "テラパゴス ex" },
};

const SET_NAMES: Record<string, { set: NameSet; sris: NameSet }> = {
  "set-sv4a-ko": {
    set: { ko: "샤이니 트레저 ex", en: "Shiny Treasure ex", ja: "シャイニートレジャーex" },
    sris: { ko: "스칼렛&바이올렛", en: "Scarlet & Violet", ja: "スカーレット&バイオレット" },
  },
  "set-sv8a-ko": {
    set: { ko: "테라스탈 페스타 ex", en: "Terastal Festival ex", ja: "テラスタルフェスex" },
    sris: { ko: "스칼렛&바이올렛", en: "Scarlet & Violet", ja: "スカーレット&バイオレット" },
  },
  "set-s12a-ko": {
    set: { ko: "VSTAR 유니버스", en: "VSTAR Universe", ja: "VSTARユニバース" },
    sris: { ko: "소드&실드", en: "Sword & Shield", ja: "ソード&シールド" },
  },
};

/**
 * 이름 필드를 **읽는 시점에 현재 표시 언어로** 해석되게 바꾼다.
 *
 * 필드를 게터로 덮어쓰는 이유: `card.cardNm` 을 읽는 곳이 30곳 가까이 되는데,
 * 전부 함수 호출로 바꾸면 변경 범위가 화면 전체로 번진다. 값의 모양(string)은
 * 그대로 두고 해석만 늦추면 호출부를 하나도 건드리지 않아도 된다.
 * 언어를 바꾸면 i18next 가 화면을 다시 그리므로 게터가 새 값을 돌려준다.
 */
function localizeName<T extends object>(target: T, key: keyof T, names: NameSet): void {
  Object.defineProperty(target, key, {
    get: () => names[currentLanguage()] ?? names.ko,
    enumerable: true,
    configurable: true,
  });
}

for (const card of DUMMY_CARDS) {
  const names = CARD_NAMES[card.cardUid];
  if (names) localizeName(card, "cardNm", names);
}
for (const set of DUMMY_SETS) {
  const names = SET_NAMES[set.cardsetUid];
  if (!names) continue;
  localizeName(set, "cardsetNm", names.set);
  localizeName(set, "srisNm", names.sris);
}

export const DUMMY_HOLDINGS: DummyHolding[] = [
  { holdingId: 1, cardUid: "card-sv4a-205-ko", qty: 1, purchPrcAmt: 52000, purchDt: "2026-03-14", graderCd: "RAW", grdCd: "-", scanPrcAmt: 61000 },
  { holdingId: 2, cardUid: "card-sv4a-350-ko", qty: 1, purchPrcAmt: 480000, purchDt: "2026-05-02", graderCd: "PSA", grdCd: "10", scanPrcAmt: 470000 },
  { holdingId: 3, cardUid: "card-s12a-223-ko", qty: 2, purchPrcAmt: 76000, purchDt: "2026-06-21", graderCd: "RAW", grdCd: "-", scanPrcAmt: 82000 },
  // 스캔 시점가가 없는 보유분 — 예전에 담겼거나 검색으로 담은 카드. 수익률을 낼 수 없다
  { holdingId: 4, cardUid: "card-sv8a-215-ko", qty: 1, purchPrcAmt: null, purchDt: "", graderCd: "RAW", grdCd: "-" },
  { holdingId: 5, cardUid: "card-s12a-172-ko", qty: 1, purchPrcAmt: 41000, purchDt: "2026-07-02", graderCd: "RAW", grdCd: "-", scanPrcAmt: 39500 },
];

/**
 * 관심(즐겨찾기) 카드 — SC-019 §3 (FN-049).
 * 보유와 무관하다. 사는 걸 고민 중인 카드가 여기 들어간다.
 */
export const DUMMY_WATCHLIST: string[] = [
  "card-sv8a-215-ko",
  "card-sv4a-350-ko",
  "card-s12a-172-ko",
];

/**
 * 최근 스캔 이력 — SC-019 §4 (FN-050).
 * **저장 여부와 무관**하게 인식한 순서대로 쌓이고, 최신 10건만 남는다.
 * 최신순(맨 앞이 가장 최근).
 */
export const DUMMY_RECENT_SCANS: string[] = [
  "card-sv4a-205-ko",
  "card-sv8a-091-ko",
  "card-s12a-223-ko",
  "card-sv8a-215-ko",
  "card-sv4a-350-ko",
  "card-sv8a-142-ko",
  "card-s12a-172-ko",
];

export const DUMMY_COLLECTIONS: DummyCollection[] = [
  { coletId: 1, coletNm: "이브이 파생 모으기", cardUids: ["card-sv8a-091-ko"] },
  { coletId: 2, coletNm: "SAR 도감", cardUids: ["card-sv4a-350-ko", "card-s12a-223-ko", "card-sv8a-215-ko"] },
];

/* ============================================================================
 * 개발 빌드 전용 채움 — **광고 자리를 눈으로 보려고** 넣는다 (2026-08-27).
 *
 * 인피드 광고는 SC-006 세트 목록에서 13번째, SC-002 내 컬렉션에서 4번째부터
 * 붙는다(수익모델 §3.1). 그런데 더미 카탈로그는 세트당 2~3장, 컬렉션 3개라
 * **광고가 한 번도 안 나온다** — 자리를 옮기거나 밀도를 바꿔도 확인할 방법이
 * 없었다.
 *
 * ⚠ 개발 빌드에서만 채운다. 실 카탈로그(pr_card_varnt_prc)가 붙으면 이 블록을
 *   통째로 지운다 — assets/dev-cards 와 같은 운명이다.
 * ⚠ 이름·번호는 '샘플'임이 드러나게 둔다. 진짜 카드처럼 보이면 나중에 데이터
 *   문제로 오해된다.
 * ============================================================================ */
if (import.meta.env.DEV) {
  /* ⚠ 아트워크는 없다 — dev 는 임시 더미 그림(assets/dev-cards/*.webp)을 걷어냈다.
     colorKey 그라디언트가 그대로 카드 면이 된다 (CardArt 폴백). */
  const FILLER_COLORS = ["fire", "thunder", "psychic", "steel", "normal", "grass"];

  /** 세트 하나를 20장까지 채운다 — 13번째 광고 자리를 지나려면 그만큼 필요하다 */
  for (let i = 1; i <= 18; i += 1) {
    const no = String(i).padStart(3, "0");
    DUMMY_CARDS.push({
      cardUid: `card-sv8a-dev-${no}`,
      cardsetUid: "set-sv8a-ko",
      cardNm: `샘플 카드 ${i}`,
      cardNo: `${no}/187`,
      langCd: "ko",
      rarityCd: i % 5 === 0 ? "SAR" : i % 3 === 0 ? "SR" : "R",
      colorKey: FILLER_COLORS[i % FILLER_COLORS.length],
      listPrcAmt: 3000 + i * 1700,
      cardTypeCd: "POKEMON",
      finishCds: ["HOLOFOIL"],
      variantPrices: [
        {
          graderCd: "RAW",
          grdCd: "-",
          prcAmt: 3000 + i * 1700,
          currcyCd: "KRW",
          trendRt: ((i % 7) - 3) * 1.4,
        },
      ],
      variants: [{ varntCd: "DEFAULT", label: "기본판" }],
    });
  }

  /** 컬렉션도 6개까지 — 4번째 뒤에 광고가 붙는 것을 보려면 5개는 넘어야 한다 */
  for (let i = 3; i <= 6; i += 1) {
    DUMMY_COLLECTIONS.push({
      coletId: i,
      coletNm: `샘플 컬렉션 ${i - 2}`,
      cardUids: ["card-sv8a-215-ko", `card-sv8a-dev-${String(i).padStart(3, "0")}`],
    });
  }
}

// ─── 조회 헬퍼 (API 교체 시 시그니처 유지) ───────────────────────────

export function findCard(cardUid: string): DummyCard | undefined {
  return DUMMY_CARDS.find((c) => c.cardUid === cardUid);
}

export function findSet(cardsetUid: string): DummyCardSet | undefined {
  return DUMMY_SETS.find((s) => s.cardsetUid === cardsetUid);
}

export function cardsInSet(cardsetUid: string): DummyCard[] {
  return DUMMY_CARDS.filter((c) => c.cardsetUid === cardsetUid);
}

export function searchCards(query: string): DummyCard[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return DUMMY_CARDS.filter(
    (c) => c.cardNm.toLowerCase().includes(q) || c.cardNo.includes(q),
  );
}

/**
 * 고액 병기 표기 (PRD §3.2 "₩ 및 만/억 단위", IA §4.3 "고액은 만/억 단위 병기").
 * 1만원 미만은 원 표기가 이미 짧아 `null` 을 돌려주고 병기하지 않는다.
 *
 * ⚠ **단위는 로케일이 정한다**(2026-09-14). 종전엔 만·억을 문자열로 직접 붙여, 영어로
 *   보는 사용자에게도 마이 페이지 평가액이 "1.7만" 으로 찍혔다 — 만·억은 한자문화권
 *   단위라 영어에는 없는 자릿수다. `Intl` 이 ko "1.7만" · ja "1.7万" · en "17.3K" 를
 *   각각 만든다(조·兆·T 까지 알아서 올라간다 — 종전 코드는 억에서 멈춰 "10,000억"이었다).
 *
 * ⚠ 옵션 두 개는 **한국어 표기를 종전과 한 글자도 다르지 않게** 두려고 붙였다:
 *   `maximumFractionDigits:1` 이 없으면 12,345,678 이 "1235만"(반올림)이 되고,
 *   `useGrouping` 을 안 켜면 "1234.6만" 으로 천 단위 쉼표가 빠진다(compact 의 기본값은
 *   `"min2"`). `true` 는 스펙상 `"always"` 로 정규화된다 — 문자열을 그대로 쓰면
 *   현재 TS lib 의 Intl 타입(boolean)과 어긋난다.
 */
export function formatKrwCompact(amount: number): string | null {
  if (Math.abs(amount) < 10_000) return null;
  return new Intl.NumberFormat(currentLanguage(), {
    notation: "compact",
    maximumFractionDigits: 1,
    useGrouping: true,
  }).format(amount);
}

/** 세트코드-카드번호 티커 (SC-019 §2 심플 뷰) — 예: SV4A-205 */
export function cardTicker(card: DummyCard): string {
  const set = findSet(card.cardsetUid);
  const no = card.cardNo.split("/")[0];
  return `${(set?.cardsetCd ?? "").toUpperCase()}-${no}`;
}

/** colorKey → CSS gradient (이미지 플레이스홀더) */
export const CARD_GRADIENTS: Record<string, string> = {
  fire: "linear-gradient(160deg, #ff9a56, #d43f00)",
  thunder: "linear-gradient(160deg, #ffe259, #ffa751)",
  psychic: "linear-gradient(160deg, #c471ed, #6f2da8)",
  steel: "linear-gradient(160deg, #cfd9df, #8899a6)",
  normal: "linear-gradient(160deg, #e8d9c4, #b0977a)",
  grass: "linear-gradient(160deg, #96e6a1, #2f9e44)",
};
